import React, { useState } from 'react'
import { graphql } from 'gatsby'
import styled from 'styled-components'

import Layout from '../../components/Layout/Layout'
import Hero, { HeroVariantEnum } from '../../components/Hero/Hero'
import pxToRem from '../../utils/pxToRem'
import { deviceMax } from '../../theme/breakpoints'
import CKEditorContent from '../../components/CKEditorContent/CKEditorContent'
import Seo from '../../components/Seo/Seo'
import CardContact from '../../components/CardContact/CardContact'
import SideNavigation, { SideNavTypeEnum } from '../../components/SideNavigation/SideNavigation'
import { MainContainer } from '../../theme/mainContainer.styled'
import Heading from '../../components/Heading/Heading'
import HeadingSizeEnum from '../../enums/headingSize'
import Button from '../../components/Button/Button'
import ButtonVariantEnum from '../../enums/ButtonVariant'
import FooterModal from '../../components/FooterModal/FooterModal'
import NotFoundPage from '../404'
import { Helmet } from 'react-helmet'
import useMediaQuery from '../../hooks/useMediaQuery'

const SUBSCRIBE_ALERTS_LABEL = 'Subscribe to Constellium alerts'

const StyledArticleBody = styled.div`
  flex-direction: column;
  display: flex;
  padding: ${pxToRem(20)};
  border-radius: ${pxToRem(5)};
  background-color: ${({theme}) => theme.colors.unique.white};
  @media ${deviceMax.tablet} {
    border-radius: 0;
  }
`

const StyledRight = styled.div`
  width: 100%;
  max-width: ${pxToRem(792)};
  flex-direction: column;
  display: flex;
  gap: ${pxToRem(20)};
`

const StyledPage = styled(MainContainer)`
  margin: 0 auto;
  display: flex;
  padding: ${pxToRem(10)} 0;

  @media ${deviceMax.mobileL} {
    flex-direction: column;
  }
`

const StyledLeftSide = styled.div`
  width: 100%;
  max-width: ${pxToRem(384)};
  margin-right: ${pxToRem(20)};
`

const StyledSubcribeAlerts = styled(Button)`
  @media ${deviceMax.mobileL} {
    justify-content: center;
    margin: 0 ${pxToRem(10)};
    width: calc(100% - ${pxToRem(20)});
  }
`
const ContactsCardsContainerDesktop = styled.div`
  display: block;
  margin-top: ${pxToRem(20)};
  @media ${deviceMax.mobileL} {
    display: none;
  }
`

const ContactsCardsContainerMobile = styled.div`
  display: none;
  @media ${deviceMax.mobileL} {
    justify-content: center;
    display: flex;
  }
`

const StyledCardContact = styled(CardContact)`
  @media ${deviceMax.mobileL} {
    margin: 0 ${pxToRem(10)};
  }
`

const PageRequestDocument = ({data, location}) => {
  const [show, setShow] = useState(false)

  if (data.contentfulPageRequestDocument === null) {
    return <NotFoundPage location={location}/>
  }
  const {
    pageTitle,
    backgroundColor,
    imageHeaderDesktop,
    imageHeaderMobile,
    blocTitle,
    blocText,
    seoMetaTitle,
    seoMetaDescription,
  } = data.contentfulPageRequestDocument

  const {contact} = data.contentfulPageInvestors

  const isMobile = useMediaQuery(deviceMax.mobileL)

  const blocTextContent = blocText ? blocText.content : ''

  const contactsCards =
    contact &&
    contact.map((contact) => {
      return (
        <StyledCardContact
          department={contact.department}
          email={contact.email}
          firstname={contact.firstname}
          key={contact.id}
          lastname={contact.lastname}
          phone={contact.phone}
        />
      )
    })

  return (
    <Layout>
      <Helmet>
        <meta name="robots" content="noindex"/>
      </Helmet>
      <Seo
        title={seoMetaTitle ? seoMetaTitle.seoMetaTitle : pageTitle}
        description={seoMetaDescription?.seoMetaDescription}
        canonicalHref={location.pathname}
      />
      <FooterModal
        show={show}
        isMobile={isMobile}
        onClose={() => {
          setShow(false)
        }}
      />
      <Hero
        mediaDesktop={imageHeaderDesktop}
        mediaMobile={imageHeaderMobile}
        title={pageTitle}
        backgroundColor={backgroundColor}
        pathname={location.pathname}
        variant={HeroVariantEnum.investor}
      />

      <StyledPage>
        {!isMobile && (
          <StyledLeftSide>
            <SideNavigation variant={SideNavTypeEnum.investor}/>
            {contact && (
              <ContactsCardsContainerDesktop>
                {contactsCards}
              </ContactsCardsContainerDesktop>
            )}
          </StyledLeftSide>
        )}
        <StyledRight>
          <StyledSubcribeAlerts
            onClick={() => setShow(true)}
            label={SUBSCRIBE_ALERTS_LABEL}
            variant={ButtonVariantEnum.secondary}
          />
          {contact && (
            <ContactsCardsContainerMobile>
              {contactsCards}
            </ContactsCardsContainerMobile>
          )}
          <StyledArticleBody>
            {blocTitle && (
              <Heading label={blocTitle} size={HeadingSizeEnum.h2}/>
            )}
            {blocTextContent && <CKEditorContent html={blocTextContent}/>}
          </StyledArticleBody>
        </StyledRight>
      </StyledPage>
    </Layout>
  )
}
export default PageRequestDocument

export const pageBasic = graphql`
  query PageRequestDocument {
    contentfulPageInvestors {
      contact {
          ...contactFragment
      }
    }
    contentfulPageRequestDocument {
      seoMetaTitle {
        seoMetaTitle
      }
      seoMetaDescription {
        seoMetaDescription
      }
      backgroundColor
      pageTitle
      imageHeaderDesktop {
          ...imageHeaderFragment
      }
      imageHeaderMobile {
          ...imageHeaderFragment
      }
      blocTitle
      blocText {
        content
      }
      seoMetaTitle {
        seoMetaTitle
      }
      seoMetaDescription {
        seoMetaDescription
      }
    }
  }
`
